import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import {
  getAircraftIdsQueryValue,
  getMainRouteAircraftRanges,
  getUnionRange,
  isUtilizationInRange,
} from '@lp/features/RoutePlanner/utils';
import { AircraftUtilization, AircraftUtilizationParams, RouteTypeEnum } from '@/api/apis/route';
import { CaseStatus } from '@/api/apis/case';
import { useAircraftUtilizationQuery, useRouteByType } from '@/queries/route-planner';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { isAllowedStatus } from '@/utils/helpers';

export type AircraftUtilizationMap = Map<string, AircraftUtilization[]>;

export type AircraftUtilizationIndex = {
  utilizationsByAircraftId: AircraftUtilizationMap;
  mainLegIds: ReadonlySet<string>;
};

const AircraftUtilizationContext = createContext<AircraftUtilizationIndex | null>(null);

export const AircraftUtilizationProvider = ({ children }: PropsWithChildren) => {
  const { caseId, logisticsReference, status } = useBaseCaseDetails();
  const { data: mainRoute } = useRouteByType(RouteTypeEnum.Main, logisticsReference?.id);

  const legs = mainRoute?.legs;

  const aircraftRanges = useMemo(() => getMainRouteAircraftRanges(legs || []), [legs]);
  const mainLegIds = useMemo(() => new Set((legs || []).map((l) => l.id)), [legs]);

  const isActiveCaseStatus = isAllowedStatus({
    statusFrom: CaseStatus.PendingLogistics,
    statusTo: CaseStatus.ReturningToBase,
    currentStatus: status,
  });

  const params = useMemo<AircraftUtilizationParams>(() => {
    if (!isActiveCaseStatus) return {};

    const unionRange = getUnionRange(aircraftRanges);

    if (!unionRange) return {};

    return {
      aircraftIds: getAircraftIdsQueryValue(aircraftRanges),
      arrivalTimeEqualOrGreaterThan: unionRange.from,
      departureTimeEqualOrLessThan: unionRange.to,
    };
  }, [aircraftRanges, isActiveCaseStatus]);

  const { data } = useAircraftUtilizationQuery(params, !!params.aircraftIds);

  const utilizationsByAircraftId = useMemo(() => {
    const map: AircraftUtilizationMap = new Map();

    if (!isActiveCaseStatus || !data?.length) return map;

    aircraftRanges.forEach((range, aircraftId) => {
      const utilizations = data.filter(
        (u) => u.aircraftResource.id === aircraftId && u.caseId !== caseId && isUtilizationInRange(u, range),
      );

      if (utilizations.length) {
        map.set(aircraftId, utilizations);
      }
    });

    return map;
  }, [data, aircraftRanges, caseId, isActiveCaseStatus]);

  const value = useMemo(() => ({ utilizationsByAircraftId, mainLegIds }), [utilizationsByAircraftId, mainLegIds]);

  return <AircraftUtilizationContext.Provider value={value}>{children}</AircraftUtilizationContext.Provider>;
};

export const useAircraftUtilizationContext = (): AircraftUtilizationIndex => {
  const context = useContext(AircraftUtilizationContext);

  if (!context) {
    throw new Error('useAircraftUtilizationContext must be used within AircraftUtilizationProvider');
  }

  return context;
};
